import {
  Block,
  ImageID,
  Options,
  ParsedQuestion,
  ParsedQuestionImage,
  ParsedSanityQuestionsFlow,
  TiaEntityAndField,
  Validation,
} from 'utility/node/sanity/claimQuestions.types';
import { create } from 'zustand';
import {
  getMappedTiaEntity,
  getMessageTextNew,
  getValueFromPath,
  shouldRenderQuestion,
} from 'utility/file-claim';
import _ from 'lodash';
import {
  dateValidations,
  numberValidations,
  stringValidations,
} from 'utility/validation/dynamic-validations';
import yup from 'utility/validation/yup';

export enum ChatEnums {
  METADATA = '_metadata',
  CASE_ITEM_OPTIONS = 'caseItem.options',
  CLAIM_TASK_LIST_OPTIONS = 'claimTaskList.options',
}
/*
Interface below is missing quite a few types.
Essentially it's a copy of ParsedQuestions for the most part.
If anything is missing here, it needs to be added
 */
export interface ChatMessage {
  id: string;
  isUser: boolean;
  text: Block[] | string;
  options: Options[] | null;
  questionId?: string;
  images: ParsedQuestionImage[] | null;
  requiresUserInput: boolean | null;
  inputType: string | null;
  flowId: string;
  tiaEntityAndField?: TiaEntityAndField | null;
  questionText: Block[] | string;
  metadata?: string | null;
  validations: Validation[] | null;
}

export interface SanityClaimQuestionStore {
  flows: ParsedSanityQuestionsFlow[];
  currentFlowId: string | null;
  currentQuestionId: string | null;
  currentQuestionIndex: number;
  messages: ChatMessage[]; //Used to display messages on screen
  answers: { [key: string]: any }; //Formik values // maybe could match the exact object of init data?
  submittedValues: { [key: string]: { value: string; path: string } } | null; //Used for shouldRenderQuestion logic (renderIf in Sanity)
  isSubmitting: boolean;
  setFlows: (flows: ParsedSanityQuestionsFlow[]) => void;
  submitAnswer: (answer: { [key: string]: any }, path?: string) => void;
  editAnswerFromMessageId: (
    answerId: string,
    flowId: string,
    currentValues: any,
  ) => { [key: string]: any };
  getNextQuestion: () => ParsedQuestion | null; //Does not support when last question...there's an end added, but need to look into it
  getCurrentQuestion: () => ParsedQuestion;
  getQuestionById: (questionId: string) => ParsedQuestion | null;
  setActivePolicy: (productLineId: string) => void;
  activePolicy: string | null;
  activeImageIndex: Binary;
  setActiveImageIndex: (index: Binary) => void;
  imageAreas: ImageID[];
  setImageAreas: (imageMetaData: ImageID) => void;
  svgMarkup: string[] | null;
  setSvgMarkup: (svgMarkup: string[]) => void;
  sanityValidationSchema: any | undefined;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  end: boolean;
  resetState: () => void;
}

const initialState = {
  flows: [],
  currentQuestionIndex: 0,
  currentFlowId: null,
  currentQuestionId: null,
  messages: [],
  answers: {},
  isSubmitting: false,
  activePolicy: null,
  submittedValues: null,
  activeImageIndex: 0 as Binary,
  imageAreas: [],
  svgMarkup: null,
  loading: true,
  error: null,
  end: false,
  sanityValidationSchema: undefined,
};

type Binary = 0 | 1;
export const useSanityClaimQuestionStore = create<SanityClaimQuestionStore>(
  (set, get) => {
    const getQuestionIdFromAnswer = (answerId: string) => {
      return answerId.replace(/-answer$/, '');
    };

    const constructMessageObject = <T extends boolean>({
      flow,
      message,
      isUser,
      answer,
    }: {
      flow: ParsedSanityQuestionsFlow;
      message: ParsedQuestion;
      isUser: T;
      answer?: T extends true ? any : undefined;
    }): ChatMessage => {
      const messageId = isUser ? `${message.id}-answer` : message.id;
      const messageText = isUser
        ? getMessageTextNew(message, answer)
        : message.questionText;
      const images = isUser ? null : message.image;
      const options = isUser ? null : message.options;

      return {
        ...(!isUser && message),
        id: messageId,
        isUser: isUser,
        text: messageText,
        options: options,
        images: images,
        questionId: message.questionId,
        inputType: message.inputType,
        requiresUserInput: message.requiresUserInput,
        flowId: flow.id,
      };
    };

    const findNextQuestion = (
      flow: ParsedSanityQuestionsFlow,
      index: number,
    ) => {
      const { submittedValues } = get();
      while (index < flow.questions.length) {
        const question = flow.questions[index];
        const shouldRender = shouldRenderQuestion(question, submittedValues);
        if (shouldRender) {
          return {
            question: question,
            index: index,
          };
        }
        index++;
      }
      return null;
    };

    const getFlow = (flowId?: string) => {
      const { flows, currentFlowId } = get();
      return flows.find((f) => f.id === (flowId || currentFlowId));
    };

    return {
      ...initialState,

      resetState: () => {
        set({ ...initialState });
      },

      setFlows: (flows) => {
        const { activePolicy, resetState } = get();
        resetState();
        const firstFlow = flows.find(
          (flow) => flow.prodLineId === activePolicy,
        );

        const firstQuestion = firstFlow?.questions[0];
        if (!firstFlow || !firstQuestion) return;

        const validationSchema = buildValidationSchema(firstFlow.questions);

        set({
          sanityValidationSchema: validationSchema,
        });

        const initialMessage: ChatMessage = constructMessageObject({
          flow: firstFlow,
          message: firstQuestion,
          isUser: false,
        });

        set({
          flows,
          currentFlowId: firstFlow.id,
          currentQuestionId: firstQuestion.id,
          currentQuestionIndex: 0,
          messages: [initialMessage],
          answers: {},
        });
      },

      getNextQuestion: () => {
        const {
          flows,
          currentQuestionId,
          messages,
          activePolicy,
          currentQuestionIndex,
        } = get();

        const currentFlow = getFlow();

        if (!currentFlow || !currentQuestionId) return null;

        const currentQuestion = currentFlow.questions[currentQuestionIndex];

        if (currentQuestion.isLastQuestion) {
          if (activePolicy && currentFlow.prodLineId !== activePolicy) {
            const nextFlow = flows.find(
              (flow) => flow.prodLineId === activePolicy,
            );

            if (!nextFlow) {
              set({
                end: true,
              });
              return null;
            }

            const firstQuestion = findNextQuestion(nextFlow, 0);
            set({
              currentFlowId: nextFlow.id,
              currentQuestionId: firstQuestion.question.id,
              currentQuestionIndex: firstQuestion.index,
              messages: [
                ...messages,
                constructMessageObject({
                  flow: currentFlow,
                  message: firstQuestion.question,
                  isUser: false,
                }),
              ],
            });

            return firstQuestion.question;
          }
          set({
            end: true,
          });
          return null;
        }
        const nextIndex = currentQuestionIndex + 1;

        const nextQuestion = findNextQuestion(currentFlow, nextIndex);

        //No more questions
        if (!nextQuestion) {
          set({
            end: true,
          });
          return null;
        }

        if (nextQuestion) {
          set({
            currentQuestionId: nextQuestion.question.id,
            currentQuestionIndex: nextQuestion.index,
            messages: [
              ...messages,
              constructMessageObject({
                flow: currentFlow,
                message: nextQuestion.question,
                isUser: false,
              }),
            ],
          });
        }

        return nextQuestion.question;
      },

      getCurrentQuestion: () => {
        const { currentQuestionIndex } = get();
        const currentFlow = getFlow();
        return currentFlow?.questions[currentQuestionIndex];
      },
      setActivePolicy: (productLineId: string) => {
        set({ activePolicy: productLineId });
      },
      submitAnswer: (answer, path) => {
        const {
          currentQuestionId,
          messages,
          getNextQuestion,
          submittedValues,
        } = get();

        const currentFlow = getFlow();

        if (!currentFlow || !currentQuestionId) return;
        const currentQuestion = currentFlow.questions.find(
          (q) => q.id === currentQuestionId,
        );
        if (!currentQuestion) return;
        const filteredMessages = messages.filter(
          (msg) =>
            !(
              msg.isUser &&
              getQuestionIdFromAnswer(msg.id) === currentQuestion.id
            ),
        );

        const updatedMessages: ChatMessage[] = [
          ...filteredMessages,
          constructMessageObject({
            flow: currentFlow,
            message: currentQuestion,
            isUser: true,
            answer: answer,
          }),
        ];

        set({
          answers: answer,
          messages: updatedMessages,
          submittedValues: {
            ...submittedValues,
            [currentQuestion.id]: {
              value: getValueFromPath(answer, path),
              path: path,
            },
          },
        });

        getNextQuestion();
      },
      getQuestionById: (id: string) => {
        const currentFlow = getFlow();
        return currentFlow?.questions.find((q) => q.id === id) || null;
      },

      editAnswerFromMessageId: (
        messageId: string,
        flowId: string,
        currentValues: any,
      ) => {
        const { messages, submittedValues, answers } = get();

        const questionId = getQuestionIdFromAnswer(messageId);
        const selectedFlow = getFlow(flowId);
        if (!selectedFlow) return;

        const questionIndex = selectedFlow.questions.findIndex(
          (q) => q.id === questionId,
        );
        const messageIndex = messages.findIndex((msg) => msg.id === messageId);

        if (questionIndex === -1 || messageIndex === -1) {
          return null;
        }

        const newMessages = messages.slice(0, messageIndex);

        const remainingMessages = messages.slice(messageIndex);

        let newAnswers = { ...currentValues };

        remainingMessages.forEach((message) => {
          if (!message.isUser) return;

          const messageQuestionId = getQuestionIdFromAnswer(message.id);
          const path = submittedValues[messageQuestionId]?.path;
          if (!path) return;
          const messageQuestion = selectedFlow.questions.find(
            (q) => q.id === messageQuestionId,
          );

          const isCaseItem = !!messageQuestion.caseItemOptions;

          if (isCaseItem) {
            delete answers['caseItem'];
            delete answers._metadata['caseItem'];
          }

          //update answers object
          if (_.has(newAnswers, path)) {
            _.set(newAnswers, path, '');
          }
          //update metadata in answers object
          if (_.has(newAnswers, `_metadata.${path}`)) {
            _.unset(newAnswers, `_metadata.${path}`);
          }

          //remove from submitted values
          delete submittedValues[messageQuestionId];

          //check if question had any images
          const hasImage = !!selectedFlow.questions.find(
            (q) => q.id === messageQuestionId,
          ).image;

          if (hasImage) {
            //clean images
            set({ imageAreas: [] });
          }
        });

        set({
          currentQuestionId: questionId,
          currentFlowId: selectedFlow.id,
          currentQuestionIndex: questionIndex,
          messages: newMessages,
          answers: newAnswers,
        });

        return newAnswers;
      },
      setActiveImageIndex: (index: Binary) => set({ activeImageIndex: index }),
      setSvgMarkup: (svg: string[]) => {
        set({ svgMarkup: svg });
      },

      setImageAreas: (imageMetaData) => {
        const { imageAreas } = get();

        const exists = imageAreas.some(
          (imageArea) => imageArea.id === imageMetaData.id,
        );

        set({
          imageAreas: exists
            ? imageAreas.filter(
                (imageArea) => imageArea.id !== imageMetaData.id,
              )
            : [...imageAreas, imageMetaData],
        });
      },
      setLoading: (isLoading: boolean) => {
        set({ loading: isLoading });
      },
    };
  },
);

const buildValidationSchema = (questions: ParsedQuestion[]) => {
  const validationObject = {};

  questions
    .filter((q) => q.requiresUserInput && q.inputType)
    .forEach((q) => {
      const tiaEntityMap = getMappedTiaEntity(q);
      _.set(validationObject, tiaEntityMap, tiaEntityMap);
    });

  const buildValidationSchema = () => {
    const buildSchema = (obj: {}) => {
      return Object.keys(obj).reduce((acc, k) => {
        const value = obj[k];
        const isArray = Array.isArray(value);
        const valueType = typeof value;

        if (valueType === 'string') {
          const question = questions.find((q) => {
            const tiaEntityMap = getMappedTiaEntity(q) || q.questionId;
            return tiaEntityMap === value;
          });

          //Fallback if validation type is set to 'required' but no error message is provided
          if (
            Array.isArray(question.validations) &&
            question.validations.some(
              (validation) =>
                validation.type === 'required' && !validation.errorMessage,
            )
          ) {
            question.validations.forEach((validation) => {
              if (validation.type === 'required' && !validation.errorMessage) {
                validation.errorMessage = 'Spørgsmålet skal besvares';
              }
            });
          }
          switch (question.inputType) {
            case 'text':
            case 'string':
            case 'radio':
            case 'options':
            case 'action':
            case 'autocomplete':
            case 'list':
              acc[k] = stringValidations(question.validations);
              break;
            case 'number':
              acc[k] = numberValidations(question.validations);
              break;
            case 'date':
              acc[k] = dateValidations(question.validations);
              break;
          }
        } else if (isArray) {
          acc[k] = yup.array().of(yup.object(buildSchema(value[0])));
        } else if (valueType === 'object' && !isArray) {
          acc[k] = yup.object().shape(
            Object.keys(value).reduce((innerAcc, innerKey) => {
              innerAcc[innerKey] = yup.mixed();
              return innerAcc;
            }, {}),
          );
        }
        return acc;
      }, {});
    };
    return yup.object().shape(buildSchema(validationObject));
  };

  return buildValidationSchema();
};
